import {agentTaskQueryKey, fetchTasksBatch} from '@github-ui/agent-sessions/hooks/use-task'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import {isSessionState, type SessionState} from '@github-ui/agent-sessions/types/session'
import {getAPITaskState, getAPITaskUpdatedAt} from '@github-ui/agent-sessions/utils/task-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import isEqual from 'lodash-es/isEqual'
import {useEffect, useEffectEvent, useMemo} from 'react'

import type {AgentTask} from '../../api/memex-items/contracts'
import {getVisibleAgentTasks, isUnhydratedAgentTask} from '../../components/table/agent-task-utils'
import type {MemexItemModel} from '../../models/memex-item-model'
import {updateAgentTasksForItemInQueryClient} from '../../state-providers/memex-items/query-client-api/memex-items/update-items'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'

/**
 * A unique unhydrated agent task to observe, along with every item that
 * references it (the same task id can appear on more than one item).
 */
interface TaskObservation {
  taskId: string
  owner: string
  repo: string
  itemIds: Set<number>
}

/** State resolved from a fetched CAPI task, ready to merge into the cache. */
interface HydratedTaskFields {
  state: SessionState
  title?: string
  createdAt?: string
  lastUpdatedAt?: string
}

/**
 * Resolves the `owner`/`repo` for a task, preferring the task's `repoNwo`
 * (which the backend always sends) and falling back to the item's
 * issue-derived name-with-owner.
 */
function resolveOwnerRepo(task: AgentTask, model: MemexItemModel): {owner: string; repo: string} | undefined {
  if (task.repoNwo) {
    const [owner, repo] = task.repoNwo.split('/')
    if (owner && repo) {
      return {owner, repo}
    }
  }

  const nameWithOwner = model.getNameWithOwner?.()
  if (nameWithOwner) {
    const [owner, repo] = nameWithOwner
    if (owner && repo) {
      return {owner, repo}
    }
  }

  return undefined
}

/**
 * Collects the unique unhydrated agent tasks worth observing across the loaded
 * items. Scoped to the *visible* tasks per item (`getVisibleAgentTasks`, capped
 * at MAX_AGENT_TASK_SUB_ROWS) so we never fetch state for tasks that aren't
 * rendered — a large board with many tasks per item would otherwise fire a
 * fetch storm.
 */
function collectTaskObservations(items: Readonly<Array<MemexItemModel>>): Array<TaskObservation> {
  const byTaskId = new Map<string, TaskObservation>()

  for (const model of items) {
    for (const task of getVisibleAgentTasks(model, true)) {
      if (!task.id || !isUnhydratedAgentTask(task)) continue

      const existing = byTaskId.get(task.id)
      if (existing) {
        existing.itemIds.add(model.id)
        continue
      }

      const ownerRepo = resolveOwnerRepo(task, model)
      if (!ownerRepo) continue

      byTaskId.set(task.id, {
        taskId: task.id,
        owner: ownerRepo.owner,
        repo: ownerRepo.repo,
        itemIds: new Set([model.id]),
      })
    }
  }

  return [...byTaskId.values()]
}

/**
 * Pure transform: writes freshly fetched state (and any newly available
 * metadata) onto the matching task, clearing a prior `hydrationFailed` marker.
 * Every other task on the item is left untouched.
 */
export function setHydratedState(
  tasks: Array<AgentTask>,
  taskId: string,
  fields: HydratedTaskFields,
): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId) return task

    // Drop the transient `hydrationFailed` flag now that we have real state.
    const {hydrationFailed: _hydrationFailed, ...rest} = task
    return {
      ...rest,
      state: fields.state,
      title: fields.title ?? task.title,
      createdAt: fields.createdAt ?? task.createdAt,
      lastUpdatedAt: fields.lastUpdatedAt ?? task.lastUpdatedAt,
    }
  })
}

/**
 * Pure transform: marks the matching task as having failed hydration so the
 * render path hides it instead of showing a stuck loading pill. Returns the
 * same array contents when the task is already marked (so the cache write is a
 * no-op).
 */
export function markHydrationFailed(tasks: Array<AgentTask>, taskId: string): Array<AgentTask> {
  return tasks.map(task => {
    if (task.id !== taskId || task.hydrationFailed) return task
    return {...task, hydrationFailed: true}
  })
}

function resolveHydratedFields(apiTask: HydratedAPITask): HydratedTaskFields | undefined {
  const rawState = getAPITaskState(apiTask)
  if (!isSessionState(rawState)) return undefined

  return {
    state: rawState,
    title: apiTask.name,
    createdAt: apiTask.created_at,
    lastUpdatedAt: getAPITaskUpdatedAt(apiTask),
  }
}

/**
 * Lazy-loads the state of agent assignment tasks on the client.
 *
 * When `memex_lazy_hydrate_agent_tasks` is enabled, the backend may send agent
 * tasks with an `id` and agent metadata but no `state` (the "unhydrated"
 * window). This hook collects those tasks across the loaded items, resolves
 * their state with a single batched React Query (via `@github-ui/agent-sessions`'s
 * `fetchTasksBatch`), and merges the resolved state back into the cached items
 * so the board/table/roadmap pills can render the real status.
 *
 * Batching keeps request volume flat: many unhydrated tasks resolve in one
 * request instead of N concurrent single-task GETs, avoiding the 404/429/timeout
 * failures those would surface during memex live updates. The query is keyed by
 * the sorted set of unhydrated ids so the same set always maps to one call.
 *
 * Behaviour: the batch query retries transient failures with backoff; a task
 * renders a loading pill while the batch is pending/retrying and is hidden via
 * `markHydrationFailed` once it terminally fails or is absent from a successful
 * response; live-update refetches naturally re-hydrate. Each resolved task is
 * also written into its per-task `agentTaskQueryKey` cache so `useTask`
 * consumers stay warm.
 *
 * It is a complete no-op when the flag is off or when there are no unhydrated
 * tasks, so it is safe to merge and roll out independently of the backend change.
 */
export function useHydrateAgentTasks(): void {
  const lazyHydrationEnabled = isFeatureEnabled('memex_lazy_hydrate_agent_tasks')
  const queryClient = useQueryClient()
  const {items} = useMemexItems()

  const observations = useMemo<Array<TaskObservation>>(() => {
    if (!lazyHydrationEnabled) return []
    return collectTaskObservations(items)
  }, [lazyHydrationEnabled, items])

  // Stable, sorted set of unhydrated task ids. Sorting makes the batch query key
  // order-independent, so the same set of tasks always maps to one cache entry
  // even when items load in a different order.
  const taskIds = useMemo(() => observations.map(observation => observation.taskId).sort(), [observations])

  const batchEnabled = lazyHydrationEnabled && taskIds.length > 0

  const {data, status, dataUpdatedAt} = useQuery({
    queryKey: ['agent-tasks-batch', taskIds],
    queryFn: ({signal}: {signal?: AbortSignal}) => fetchTasksBatch({taskIds, signal}),
    enabled: batchEnabled,
  })

  // Re-run the write-back when the batch resolves/fails (status or data version
  // changes) or when an already-resolved task gains a new referencing item. The
  // same task can appear on multiple items, and a later-loading item won't
  // change the batch's status or `dataUpdatedAt`, so referencing `itemIds` are
  // part of the signature. Re-running for already-merged items is safe: the
  // write-back is idempotent (guarded by `isEqual`).
  const writeBackSignature = `${status}:${dataUpdatedAt}:${observations
    .map(observation => `${observation.taskId}@${[...observation.itemIds].sort((a, b) => a - b).join(',')}`)
    .join('|')}`

  // `observations`/`data` are read from the current render; the effect below
  // re-runs whenever `writeBackSignature` changes (a status/data version or item
  // set change), which is exactly when we want to re-run the write-back.
  const writeBackHydratedTasks = useEffectEvent(() => {
    // While the batch is in flight (including retries) leave the tasks loading.
    if (status === 'pending') return

    // Index the fetched tasks by id for O(1) lookup during fan-out. A task that
    // is absent from a successful response was not found by the batch endpoint.
    const tasksById = new Map<string, HydratedAPITask>()
    if (status === 'success' && data) {
      for (const task of data) {
        if (task.id) tasksById.set(task.id, task)
      }
    }

    for (const observation of observations) {
      const {taskId, owner, repo, itemIds} = observation
      const fetched = tasksById.get(taskId)

      if (fetched) {
        // Keep any `useTask` consumer warm by seeding the per-task cache. Guard
        // with `isEqual` so an unchanged value doesn't churn observers.
        const taskKey = agentTaskQueryKey(owner, repo, taskId)
        // eslint-disable-next-line no-restricted-syntax -- per-task cache lives in @github-ui/agent-sessions and has no memex query-client wrapper.
        const existing = queryClient.getQueryData<HydratedAPITask>(taskKey)
        if (!isEqual(existing, fetched)) {
          // eslint-disable-next-line no-restricted-syntax -- per-task cache lives in @github-ui/agent-sessions and has no memex query-client wrapper.
          queryClient.setQueryData(taskKey, fetched)
        }

        const fields = resolveHydratedFields(fetched)
        if (fields) {
          for (const itemId of itemIds) {
            updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks => setHydratedState(tasks, taskId, fields))
          }
          continue
        }
      }

      // Terminal for hydration: the whole batch failed (`status === 'error'`),
      // or the task was missing from / returned without a usable state in a
      // successful response. Mark it failed so it is hidden rather than stuck on
      // a loading pill.
      for (const itemId of itemIds) {
        updateAgentTasksForItemInQueryClient(queryClient, itemId, tasks => markHydrationFailed(tasks, taskId))
      }
    }
  })

  useEffect(() => {
    if (!batchEnabled) return
    writeBackHydratedTasks()
  }, [batchEnabled, queryClient, writeBackSignature])
}

/**
 * Headless host for {@link useHydrateAgentTasks}. Rendered once near the top of
 * the project view so the React Query observers it owns re-render this tiny
 * component (which returns `null`) rather than the much larger view tree.
 */
export function AgentTaskHydrator(): null {
  useHydrateAgentTasks()
  return null
}
